import React, { useEffect, useState } from 'react';
import { Mail, MailX, MailWarning, RefreshCw } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const KIND_LABELS = {
  manual: 'Manual',
  bulk: 'Bulk',
  reminder: 'Release reminder',
  upcoming: 'Recording reminder',
  thank_you: 'Thank-you',
  confirmation: 'Booking confirmation',
  outreach: 'Prospect outreach',
};

function StatusBadge({ status }) {
  if (status === 'sent') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10.5px]"><Mail className="w-3 h-3 mr-1" /> Sent</Badge>;
  if (status === 'failed') return <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10.5px]"><MailX className="w-3 h-3 mr-1" /> Failed</Badge>;
  return <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10.5px]"><MailWarning className="w-3 h-3 mr-1" /> {status}</Badge>;
}

export default function EmailHistoryList({ fetcher, dataTestid = 'email-history' }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetcherRef = React.useRef(fetcher);
  fetcherRef.current = fetcher;

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetcherRef.current();
      setItems(Array.isArray(r) ? r : []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid={dataTestid} className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
          Email history {items && `· ${items.length}`}
        </div>
        <Button variant="ghost" size="sm" className="h-7 text-stone-600 text-[12px]" onClick={load} disabled={loading} data-testid="email-history-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>
      {items === null && (
        <div className="text-stone-400 text-[13px] py-6 text-center">Loading…</div>
      )}
      {items && items.length === 0 && (
        <div className="text-stone-400 text-[13px] py-8 text-center border border-dashed border-stone-200 rounded-md">
          No emails sent to this contact yet.
        </div>
      )}
      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map(it => {
            const when = it.sentAt ? new Date(it.sentAt) : null;
            return (
              <li key={it.id} className="border border-stone-200 rounded-md p-3 hover:bg-stone-50" data-testid="email-history-item">
                <div className="flex items-start gap-2">
                  <StatusBadge status={it.status} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-stone-900 truncate" title={it.subject}>{it.subject || '(no subject)'}</div>
                    <div className="text-[11.5px] text-stone-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>To: {it.to}</span>
                      {(it.cc && it.cc.length > 0) && <span>Cc: {it.cc.join(', ')}</span>}
                      {it.kind && <span>{KIND_LABELS[it.kind] || it.kind}</span>}
                      {when && <span>{when.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>}
                    </div>
                    {it.bodyPreview && (
                      <div className="text-[11.5px] text-stone-600 mt-1.5 line-clamp-2 whitespace-pre-wrap">{it.bodyPreview}</div>
                    )}
                    {it.error && (
                      <div className="text-[11px] text-rose-600 mt-1">Error: {it.error}</div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
